import { HStack } from "@chakra-ui/react";
import { FilterAltOutlined as FilterAltOutlinedIcon } from "@mui/icons-material";
import { FC, Suspense, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { ABTestDefaultReportFilterButton_Query } from "~/src/__relay_artifacts__/ABTestDefaultReportFilterButton_Query.graphql";
import { IconButton } from "~/src/components/common/IconButton";
import { Modal } from "~/src/components/common/Modal";
import { PageLoading } from "~/src/components/common/PageLoading";
import { FilteredTag } from "~/src/components/features/filter";
import { useOpenState } from "~/src/lib/hooks";

import { ABTestDefaultReportFilterButtonForm } from "./ABTestDefaultReportFilterButtonForm";
import { ABTestReportFilterFormValues as FormValues } from "./types";

export type Props = {
  filterValues: FormValues;
  onSubmit: (values: FormValues) => Promise<void> | void;
};

const query = graphql`
  query ABTestDefaultReportFilterButton_Query($slug: String!) {
    sources {
      edges {
        node {
          id
          name
        }
      }
    }
    site(slug: $slug) {
      funnels {
        edges {
          node {
            id
            name
          }
        }
      }
      abtestScenarioPages {
        edges {
          node {
            id
            title
            page {
              kind
              name
              sortNumber
              funnel {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const ABTestDefaultReportFilterButton: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ filterValues, onSubmit }) => {
  const { siteSlug = "" } = useParams();
  const { isOpen, onClose, onOpen } = useOpenState();

  const data = useLazyLoadQuery<ABTestDefaultReportFilterButton_Query>(query, {
    slug: siteSlug,
  });

  const sourceOptions = useMemo(
    () => [
      { label: "すべて", value: null },
      ...(data.sources.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }),
    ],
    [data.sources.edges]
  );

  const funnelOptions = useMemo(() => {
    return (
      data.site.funnels?.edges?.map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }) || []
    );
  }, [data.site.funnels]);

  const pageKindOptions = useMemo(
    () => [{ label: "すべて", value: null }, ...GraphQLEnums.PageKind],
    []
  );

  const deviceOptions = useMemo(
    () => [{ label: "すべて", value: null }, ...GraphQLEnums.Device],
    []
  );

  const abtestScenarioPageOptions = useMemo(() => {
    return [
      ...(data.site.abtestScenarioPages.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");

        const pageName =
          node.page.kind === "FORM"
            ? `${node.page.name}(${(node.page.sortNumber - 1).toString()})`
            : node.page.name;
        return {
          label: `${node.title || ""}(${node.page.funnel.name})[${pageName}]`,
          value: node.id,
          funnelId: node.page.funnel.id,
          pageKind: node.page.kind,
        };
      }),
    ];
  }, [data.site.abtestScenarioPages]);

  return (
    <HStack>
      {filterValues.funnelIds.length > 0 &&
        filterValues.funnelIds.map((funnelId, index) => (
          <FilteredTag
            key={index}
            name="ファネル"
            value={funnelId}
            options={funnelOptions}
          />
        ))}
      {filterValues.pageKind && (
        <FilteredTag
          name="ページタイプ"
          value={filterValues.pageKind}
          options={pageKindOptions}
        />
      )}
      {filterValues.abtestScenarioPageIds.length > 0 &&
        filterValues.abtestScenarioPageIds.map(
          (abtestScenarioPageId, index) => (
            <FilteredTag
              key={index}
              name="ABテスト"
              value={abtestScenarioPageId}
              options={abtestScenarioPageOptions}
            />
          )
        )}
      {filterValues.sourceId && (
        <FilteredTag
          name="流入元"
          value={filterValues.sourceId}
          options={sourceOptions}
        />
      )}
      {filterValues.device && (
        <FilteredTag
          name="デバイス"
          value={filterValues.device}
          options={deviceOptions}
        />
      )}
      <IconButton
        label="フィルタ"
        size="24px"
        icon={<FilterAltOutlinedIcon htmlColor="#282828" />}
        isActive={
          Object.values(filterValues).some((value) => value != null) || isOpen
        }
        onClick={onOpen}
      />
      <Modal title="フィルタ" isOpen={isOpen} onClose={onClose}>
        <Suspense fallback={<PageLoading />}>
          <ABTestDefaultReportFilterButtonForm
            filterValues={filterValues}
            onSubmit={onSubmit}
            onClose={onClose}
          />
        </Suspense>
      </Modal>
    </HStack>
  );
};
